/**
 * @overview Search page cuisine positioning behaviour.
 *
 * @module search/positionCuisines
 *
 * @see {@link https://drive.google.com/open?id=0B16QlcqNIKCuRzM5QVB2bGdGR1k Cuisine filter behaviour}
 */

import $ from 'qwery';
import { moveAfterElement, getLastElement } from '../../helpers/domHelper';
import { getCheckboxValues } from '../../helpers/checkboxHelper';
import { isListExpanded, updateExpandText } from '../../modules/controlList';


/** @constant
    @type {string}
    @default
*/
const sortIndexDataKey = 'data-sort-index';

/**
 * Places a divider between the upper and lower cuisine lists.
 *
 * @param {string|Node} context
 */
function placeCuisineDivider (context) {
    const dividerCssClass = 'u-dividerSpace';
    const divider = $('.u-dividerSpace:not([data-seo-name=all])', context)[0];
    const hideables = $('.is-hideable', context);

    if (divider) {
        divider.classList.remove(dividerCssClass);
    }

    if (hideables.length > 0) {
        const addDividerAfter = getLastElement('.is-popular, .has-moved', context);

        if (addDividerAfter) {
            addDividerAfter.classList.add(dividerCssClass);
        }
    }
}

/**
 * Bind filter events.
 *
 * @param {Node} filter
 * @param {string|Node} context
 * @param {boolean} applyFilter - Should the restaurant results be filtered?
 */
function moveItemToTopList (listItem, afterElement) {
    listItem.classList.remove('is-hideable', 'is-shown');
    listItem.classList.add('has-moved');

    moveAfterElement(listItem, afterElement);
}

/**
 * Returns the last cusine with an index which is lower than the selected cuisine index.
 *
 * @param {Node[]} cuisinesToCheck
 * @param {string} selectedCuisineIndex
 * @param {string|Node} context
 * @returns {Node}
 */
function getCuisineBefore (cuisinesToCheck, selectedCuisineIndex) {
    if (cuisinesToCheck && cuisinesToCheck.length > 0) {
        const ids = cuisinesToCheck
            // map the cuisine indexes into an array
            .map(cuisine => +cuisine.getAttribute(sortIndexDataKey))
            // filter the list of elements down to those who are positioned above the selectedCuisineIndex
            .filter(id => id < +selectedCuisineIndex);

        if (ids.length > 0) {
            // return the last item in the list
            return ids.pop();
        }
    }

    return null;
}

/**
 * Restores the position of any cuisines which have been moved previously.
 *
 * @param {Node[]} selectedCuisines
 * @param {string|Node} context
 */
function restorePreviouslyMovedCuisines (selectedCuisines, context) {
    selectedCuisines.forEach(cuisine => {
        if (cuisine.classList.contains('has-moved')) {
            const hideables = $('.is-hideable', context);
            const selectedCuisineIndex = cuisine.getAttribute(sortIndexDataKey);
            const cuisineIndexBefore = getCuisineBefore(hideables, selectedCuisineIndex);
            let cuisineBefore = $(`[${sortIndexDataKey}="${cuisineIndexBefore}"]`, context)[0];

            cuisine.classList.add('is-moving');

            if (!cuisineBefore) {
                cuisineBefore = getLastElement('.is-popular, .has-moved', context);
            }

            if (cuisineBefore === cuisine) {
                cuisineBefore = getLastElement('.is-popular:not(.is-moving), .has-moved:not(.is-moving)', context);
            }

            cuisine.classList.add('is-hideable');
            cuisine.classList.remove('has-moved');

            moveAfterElement(cuisine, cuisineBefore);

            if (isListExpanded($(context)[0])) {
                cuisine.classList.add('is-shown');
            }

            cuisine.classList.remove('is-moving');
        }
    });
}

/**
 * Ensure that the checked attribute is set correctly as the checked state can be inconsistent when the back button is used
 *
 * @param {Node} checkbox
 */
function ensureCheckedIsSetCorrectly (checkbox) {
    checkbox.checked = checkbox.hasAttribute('checked');
}

/**
 * Positions the cuisines according to certian rules.
 *
 * Cuisines are sorted into two lists; "popular" and "remainder". When a cuisine in the "remainder" list
 * is selected, it should move up to the "popular" list so that it is not hidden if the "fewer cuisines"
 * link is clicked.
 *
 * Any "remainder" cuisines which are moved up should be appended to the end of the "popular" list and
 * sorted alphabetically amongst themselves. If they are unselected, they should move back down to the
 * "remainder" list, placed alphabetically.
 *
 * @see {@link https://drive.google.com/open?id=0B16QlcqNIKCuRzM5QVB2bGdGR1k Cuisine filter behaviour}
 * @see {@link https://media.github.je-labs.com/download/user/283/files/9a27a60e-2f13-11e6-8e2c-0b019a7323bc Animated example}
 *
 * @param {string|Node} context
 * @param {boolean} [withCheckboxValidation=true] Should the checkbox states be checked?
 */
export function positionCuisines (context, { withCheckboxValidation = true } = {}) {
    const seoNames = getCheckboxValues($('.control-input', context));
    if (!seoNames) return;

    seoNames.forEach(seoName => {
        const selectedCuisine = $(`[data-seo-name="${seoName}"]`, context)[0];
        const checkbox = $('.control-input', selectedCuisine)[0];
        const movedCuisines = $('.has-moved', context);

        if (withCheckboxValidation) {
            ensureCheckedIsSetCorrectly(checkbox);
        }

        if (checkbox.value === 'all') {
            restorePreviouslyMovedCuisines(movedCuisines, context);
        } else if (checkbox.checked && selectedCuisine.classList.contains('is-hideable')) {
            // hideable cuisine was clicked
            const selectedCuisineIndex = selectedCuisine.getAttribute(sortIndexDataKey);
            const lastPopular = getLastElement('.is-popular', context);
            const cuisineIndexBefore = getCuisineBefore(movedCuisines, selectedCuisineIndex);
            const cuisineBefore = cuisineIndexBefore
                ? $(`[${sortIndexDataKey}="${cuisineIndexBefore}"]`, context)[0]
                : lastPopular;

            moveItemToTopList(selectedCuisine, cuisineBefore);
        } else {
            restorePreviouslyMovedCuisines([selectedCuisine], context);
        }
    });

    placeCuisineDivider(context);
    updateExpandText(context);
}
